/**
 * @file Components/Layouts/Header.jsx
 * @description Main navigation header with responsive mobile menu,
 * scroll-aware visibility, user auth state detection, and profile/wishlist links.
 */

import React, { useState, useEffect, useContext } from "react";
import { FiMenu, FiX } from "react-icons/fi";
import { FaHeart, FaUserCircle } from "react-icons/fa";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useSiteSettings } from "../../Context/SiteSettingsContext";
import { useAuth } from "../../Context/Auth";
import { WishlistContext } from "../../Context/WishlistContext";

const Header = () => {
  const { settings } = useSiteSettings();
  const { user } = useAuth();
  const { wishlist } = useContext(WishlistContext);
  const tagline = settings?.companyInfo?.tagline || 'Wander Wisely, Travel Boldly';
  const [isOpen, setIsOpen] = useState(false);
  const [showNavbar, setShowNavbar] = useState(window.location.pathname !== '/');
  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  const isLoggedIn = !!user;
  const displayName = user?.displayName || user?.email?.split('@')[0] || '';
  const photoURL = user?.photoURL || null;
  const wishlistCount = wishlist?.length || 0;

  useEffect(() => {
    const handleScroll = () => {
      setShowNavbar(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // On non-home pages, start collapsed; on home page, respect scroll only
  useEffect(() => {
    if (isHomePage) {
      setShowNavbar(window.scrollY > 100);
    } else {
      setShowNavbar(true);
    }
  }, [isHomePage]);

  const toggleMenu = () => setIsOpen(!isOpen);

  const handleProfileClick = () => {
    if (isLoggedIn) {
      navigate('/profile');
    } else {
      navigate('/login');
    }
  };

  return (
    <>
      {/* Main Header - Always visible on mobile, hides on scroll on desktop */}
      <header className={`fixed left-0 right-0 backdrop-blur-sm shadow-xl z-50 transition-all duration-500 ${
        showNavbar ? 'md:-top-32 md:opacity-0 top-0 opacity-100' : 'top-0 opacity-100'
      }`} style={{ background: '#4F46E5' }}>
        <div className="container mx-auto px-4 md:px-6 py-3 flex justify-between items-center relative z-10">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <img
              className="h-12 sm:h-16 md:h-20 w-auto transition-all duration-300 rounded-2xl"
              style={{ width: 'auto', minWidth: '48px' }}
              src="/assets/Asset 2Nikaba travels - Ci-1.png"
              alt="Nikaba Travels"
            />
          </Link>

          {/* Centered Tagline */}
          <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2">
            <p className="text-xl xl:text-3xl font-bold text-white tracking-wide whitespace-nowrap drop-shadow-lg" style={{ fontFamily: "'Bisky', sans-serif" }}>
              {tagline}
            </p>
          </div>

          {/* Right Side Icons */}
          <div className="flex items-center space-x-3">
            {/* Wishlist — pill with heart + counter */}
            <button 
              onClick={() => navigate('/wishlist')}
              className="group hidden md:flex items-center space-x-2 bg-white/15 hover:bg-white/25 backdrop-blur-sm rounded-full px-4 py-2 cursor-pointer transition-all duration-300 border border-white/20"
            >
              <span className="relative">
                <FaHeart className="text-base text-red-300 group-hover:text-red-200 transition-colors duration-300 group-hover:scale-110 transform" />
                <span className="absolute -top-2 -right-2.5 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center shadow-sm">{wishlistCount}</span>
              </span>
              <span className="text-sm text-white font-semibold">Wishlist</span>
            </button>

            {/* Profile — avatar + greeting */}
            <button 
              onClick={handleProfileClick}
              className="group hidden md:flex items-center space-x-2.5 bg-white/15 hover:bg-white/25 backdrop-blur-sm rounded-full pl-2 pr-4 py-1.5 cursor-pointer transition-all duration-300 border border-white/20"
            >
              {isLoggedIn && photoURL ? (
                <img 
                  src={photoURL} 
                  alt={displayName} 
                  className="w-8 h-8 rounded-full border-2 border-white/50 shadow-sm object-cover"
                  referrerPolicy="no-referrer"
                />
              ) : isLoggedIn ? (
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-400 to-blue-500 border-2 border-white/50 flex items-center justify-center shadow-sm">
                  <span className="text-white text-xs font-bold">{displayName.charAt(0).toUpperCase()}</span>
                </div>
              ) : (
                <div className="w-8 h-8 rounded-full bg-white/20 border-2 border-white/30 flex items-center justify-center">
                  <FaUserCircle className="text-lg text-white/90" />
                </div>
              )}
              <div className="text-left">
                <p className="text-[10px] text-white/60 leading-tight font-medium">
                  {isLoggedIn ? 'Welcome back' : 'Hello, Traveller'}
                </p>
                <p className="text-xs text-white font-bold leading-tight">
                  {isLoggedIn ? displayName : 'Login / Signup'}
                </p>
              </div>
            </button>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden text-white text-2xl hover:text-blue-100 transition-colors duration-300"
              onClick={toggleMenu}
              aria-label="Toggle menu"
            >
              {isOpen ? <FiX /> : <FiMenu />}
            </button>
          </div>
        </div>
      </header>

      {/* Modern Centered Navbar - Appears on Scroll with Padding - Desktop Only */}
      <div data-floating-nav className={`fixed left-0 right-0 z-40 hidden md:block transition-all duration-500 ${
        showNavbar ? 'top-6 opacity-100 translate-y-0' : '-top-20 opacity-0 -translate-y-full'
      }`}>
        <div className="container mx-auto px-4 md:px-6 flex justify-center">
          <nav className="bg-white rounded-full shadow-xl px-8 py-3 border border-gray-100">
            <div className="flex items-center justify-center space-x-1">
              <Link to="/" className="px-5 py-2 text-sm font-semibold text-gray-700 hover:text-white hover:bg-blue-600 rounded-full transition-all duration-300">
                HOME
              </Link>
              <Link to="/about" className="px-5 py-2 text-sm font-semibold text-gray-700 hover:text-white hover:bg-blue-600 rounded-full transition-all duration-300">
                ABOUT
              </Link>
              <Link to="/tours" className="px-5 py-2 text-sm font-semibold text-gray-700 hover:text-white hover:bg-blue-600 rounded-full transition-all duration-300">
                TOURS
              </Link>
              <Link to="/search" className="px-5 py-2 text-sm font-semibold text-gray-700 hover:text-white hover:bg-blue-600 rounded-full transition-all duration-300">
                BOOK
              </Link>
              <Link to="/outbound" className="px-5 py-2 text-sm font-semibold text-gray-700 hover:text-white hover:bg-blue-600 rounded-full transition-all duration-300">
                OUTBOUND
              </Link>
              <Link to="/services" className="px-5 py-2 text-sm font-semibold text-gray-700 hover:text-white hover:bg-blue-600 rounded-full transition-all duration-300">
                SERVICES
              </Link>
              <Link to="/contact" className="px-5 py-2 text-sm font-semibold text-gray-700 hover:text-white hover:bg-blue-600 rounded-full transition-all duration-300">
                CONTACT
              </Link>
              <Link to="/blogs" className="px-5 py-2 text-sm font-semibold text-gray-700 hover:text-white hover:bg-blue-600 rounded-full transition-all duration-300">
                BLOGS
              </Link>
            </div>
          </nav>
        </div>
      </div>

      {/* No spacer - each page handles its own top spacing */}

      {/* Mobile Menu */}
      <nav
        className={`md:hidden fixed top-[72px] right-3 left-3 bg-white/95 backdrop-blur-lg text-gray-800 shadow-2xl rounded-2xl transition-all duration-300 z-50 border border-gray-200 max-h-[70vh] overflow-y-auto ${
          isOpen ? "opacity-100 visible scale-100" : "opacity-0 invisible scale-95"
        }`}
      >
        {/* Close Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
          <span className="text-lg font-bold text-gray-800">Menu</span>
          <button onClick={toggleMenu} className="text-gray-700 hover:text-blue-600 text-2xl transition-colors duration-300">
            <FiX />
          </button>
        </div>

        {/* Links */}
        <ul className="flex flex-col py-4">
          <li><Link to="/" className="block py-3 px-6 hover:bg-blue-50 hover:text-blue-600 transition-all duration-300 font-medium" onClick={toggleMenu}>HOME</Link></li>
          <li><Link to="/about" className="block py-3 px-6 hover:bg-blue-50 hover:text-blue-600 transition-all duration-300 font-medium" onClick={toggleMenu}>ABOUT</Link></li>
          <li><Link to="/tours" className="block py-3 px-6 hover:bg-blue-50 hover:text-blue-600 transition-all duration-300 font-medium" onClick={toggleMenu}>TOURS</Link></li>
          <li><Link to="/search" className="block py-3 px-6 hover:bg-blue-50 hover:text-blue-600 transition-all duration-300 font-medium" onClick={toggleMenu}>BOOK</Link></li>
          <li><Link to="/outbound" className="block py-3 px-6 hover:bg-blue-50 hover:text-blue-600 transition-all duration-300 font-medium" onClick={toggleMenu}>OUTBOUND</Link></li>
          <li><Link to="/services" className="block py-3 px-6 hover:bg-blue-50 hover:text-blue-600 transition-all duration-300 font-medium" onClick={toggleMenu}>SERVICES</Link></li>
          <li><Link to="/contact" className="block py-3 px-6 hover:bg-blue-50 hover:text-blue-600 transition-all duration-300 font-medium" onClick={toggleMenu}>CONTACT</Link></li>
          <li><Link to="/blogs" className="block py-3 px-6 hover:bg-blue-50 hover:text-blue-600 transition-all duration-300 font-medium" onClick={toggleMenu}>BLOGS</Link></li>
          <li className="border-t border-gray-200 mt-2 pt-2">
            <Link to="/wishlist" className="block py-3 px-6 hover:bg-red-50 hover:text-red-600 transition-all duration-300 font-medium flex items-center" onClick={toggleMenu}>
              <FaHeart className="mr-2" /> WISHLIST
            </Link>
          </li>
          <li>
            <Link 
              to={isLoggedIn ? "/profile" : "/login"} 
              className="block py-3 px-6 hover:bg-blue-50 hover:text-blue-600 transition-all duration-300 font-medium flex items-center" 
              onClick={toggleMenu}
            >
              {isLoggedIn && photoURL ? (
                <img src={photoURL} alt="" className="w-5 h-5 rounded-full mr-2 object-cover" referrerPolicy="no-referrer" />
              ) : (
                <FaUserCircle className="mr-2" />
              )}
              {isLoggedIn ? displayName : "LOGIN"}
            </Link>
          </li>
        </ul>
      </nav>
    </>
  );
};

export default Header;